import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { EndpointsService } from '../api/endpoints.service';

const CACHE_PREFIX = 'imgcache__';
const ENABLED_KEY = 'productImagesEnabled';
const INDEX_KEY = 'imgcache__index';
const MAX_ENTRIES = 500;

@Injectable({ providedIn: 'root' })
export class ImageCacheService {
  // In-memory map: url → data-URL (avoids repeated storage reads per render cycle)
  private memCache = new Map<string, string>();
  // null = not yet loaded from storage
  private imagesEnabled: boolean | null = null;
  // Pending fetches — prevents duplicate network requests for the same URL
  private pending = new Map<string, Promise<string | null>>();

  constructor(private storage: StorageService, private endpoints: EndpointsService) {
    this.storage.getData<boolean>(ENABLED_KEY).then(v => {
      this.imagesEnabled = v !== false;
      if (this.imagesEnabled) this.warmMemCache();
    });
  }

  private async warmMemCache(): Promise<void> {
    const index: string[] = await this.storage.getData<string[]>(INDEX_KEY) || [];
    for (const url of index) {
      if (this.memCache.has(url)) continue;
      const key = CACHE_PREFIX + this.urlToKey(url);
      const stored = await this.storage.getData<string>(key);
      if (stored) this.memCache.set(url, stored);
    }
  }

  get enabled(): boolean {
    return this.imagesEnabled !== false;
  }

  async setEnabled(value: boolean): Promise<void> {
    this.imagesEnabled = value;
    await this.storage.setData(ENABLED_KEY, value);
    if (!value) {
      this.memCache.clear();
    }
  }

  /**
   * Returns a data-URL for the given image URL.
   * Returns null when images are disabled or the fetch fails.
   */
  async resolve(url: string): Promise<string | null> {
    if (!url || !this.enabled) return null;

    // 1. memory hit
    const mem = this.memCache.get(url);
    if (mem) return mem;

    // 2. already fetching
    const inFlight = this.pending.get(url);
    if (inFlight) return inFlight;

    const promise = this.loadOrFetch(url);
    this.pending.set(url, promise);
    const result = await promise;
    this.pending.delete(url);
    return result;
  }

  /** Builds a proxy URL for an external image, routing it through the backend to avoid CORS. */
  proxyUrl(url: string): string {
    if (!url) return '';
    return `${this.endpoints.kontorProxyImageBase}?url=${encodeURIComponent(url)}`;
  }

  private async loadOrFetch(url: string): Promise<string | null> {
    // 3. storage hit
    const key = CACHE_PREFIX + this.urlToKey(url);
    const stored = await this.storage.getData<string>(key);
    if (stored) {
      this.memCache.set(url, stored);
      return stored;
    }

    // 4. network fetch via backend proxy (avoids CORS on external image hosts)
    try {
      const dataUrl = await this.fetchAsDataUrl(this.proxyUrl(url));
      if (!dataUrl) return null;
      this.memCache.set(url, dataUrl);
      await this.storage.setData(key, dataUrl);
      await this.addToIndex(url);
      return dataUrl;
    } catch {
      return null;
    }
  }

  private async fetchAsDataUrl(url: string): Promise<string | null> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const resp = await fetch(url, { cache: 'default', signal: controller.signal });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } finally {
      clearTimeout(timeout);
    }
  }

  /** Prune-aware index to stay under MAX_ENTRIES */
  private async addToIndex(url: string): Promise<void> {
    let index: string[] = await this.storage.getData<string[]>(INDEX_KEY) || [];
    if (index.includes(url)) return;
    index.unshift(url);
    if (index.length > MAX_ENTRIES) {
      const evicted = index.splice(MAX_ENTRIES);
      for (const old of evicted) {
        await this.storage.removeData(CACHE_PREFIX + this.urlToKey(old));
        this.memCache.delete(old);
      }
    }
    await this.storage.setData(INDEX_KEY, index);
  }

  /** Returns cache size in entries */
  async getCacheSize(): Promise<number> {
    const index: string[] = await this.storage.getData<string[]>(INDEX_KEY) || [];
    return index.length;
  }

  /** Clears all cached images from storage and memory */
  async clearCache(): Promise<void> {
    const index: string[] = await this.storage.getData<string[]>(INDEX_KEY) || [];
    for (const url of index) {
      await this.storage.removeData(CACHE_PREFIX + this.urlToKey(url));
    }
    await this.storage.removeData(INDEX_KEY);
    this.memCache.clear();
  }

  private urlToKey(url: string): string {
    // Simple hash to keep key short and storage-safe
    let h = 0;
    for (let i = 0; i < url.length; i++) {
      h = Math.imul(31, h) + url.charCodeAt(i) | 0;
    }
    return (h >>> 0).toString(36);
  }
}
